import React, { SyntheticEvent, useContext, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { ProductPlanPeriod } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";
import { ContractStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import {
  Role,
  Subscription,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { getOrgWithSubscription } from "api/endpoints/organisation";
import { updateProductPlan } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  subscriptionTypeNameDisplayMapping,
} from "constants/index";
import {
  ContractsListContext,
  OrgDataContext,
} from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { UrlParamTypes } from "types/org-display";

const ChangeProductType = () => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);
  const { contractsList } = useContext(ContractsListContext);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const activatedContract =
    contractsList && contractsList.contracts
      ? contractsList.contracts.find(
          (e) => e.status === ContractStatus.ACTIVATED
        )
      : null;
  const isOrgOnEnterprisePlan = !!(
    activatedContract && activatedContract.hasEnterprise
  );

  const subscription = orgData.subscription && orgData.subscription[0];
  const currentSubscriptionType = subscription?.type;

  const productsAllowedForChange = [
    SubscriptionType.PREMIUM_MONTHLY,
    SubscriptionType.PREMIUM_ANNUAL,
  ];

  const targetProductType =
    productsAllowedForChange.includes(currentSubscriptionType) &&
    currentSubscriptionType === SubscriptionType.PREMIUM_MONTHLY
      ? ProductPlanPeriod.ANNUAL
      : ProductPlanPeriod.MONTHLY;
  const targetSubscriptionType =
    currentSubscriptionType === SubscriptionType.PREMIUM_MONTHLY
      ? SubscriptionType.PREMIUM_ANNUAL
      : SubscriptionType.PREMIUM_MONTHLY;

  const handleUpdateSubscriptionState = (updatedSubscription: Subscription) => {
    const newOrgData = { ...orgData } as Role;
    newOrgData.subscription[0] = updatedSubscription;
    setOrgData(newOrgData);
  };

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const handleUpdateProductPlanReq = () => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    updateProductPlan(
      {
        orgId: roleId,
        subscriptionId: subscription.id,
        product: targetProductType,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        getOrgWithSubscription(
          {
            roleId: subscription.owner,
          },
          callOptionsHeaders(regionCode, subscription.owner)
        )
          .then((resp) => {
            if (resp.role?.subscription && resp.role.subscription.length > 0) {
              handleUpdateSubscriptionState(resp.role.subscription[0]);
            }
          })
          .catch(showError);
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  const buttonDisabled =
    isSubscriptionActionLoading ||
    !productsAllowedForChange.includes(subscription?.type) ||
    !subscription?.active ||
    isOrgOnEnterprisePlan;

  return (
    <React.Fragment>
      <SubscriptionAction
        title="Product/Subscription Type:"
        middleElem={
          <Typography
            className={clsx(classes.spaceTop, classes.tableTypography)}
            data-testid="update-product-type-middle-elem"
          >
            {subscriptionTypeNameDisplayMapping[currentSubscriptionType]}
            <Tooltip title="Data from Subscription Document" placement="right">
              <IconButton
                size="small"
                sx={{ padding: "2px", marginLeft: "4px" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            disabled={buttonDisabled}
            onClick={() => handleUpdateProductPlanReq()}
            data-testid="change-product-type-btn"
          >
            Change to{" "}
            {subscriptionTypeNameDisplayMapping[targetSubscriptionType]}
          </Button>
        )}

        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={openSuccessAlert}
          autoHideDuration={DEFAULT_SNACKBAR_DURATION}
          onClose={handleClose}
          data-testid="snackbar-change-product-type"
        >
          <Alert severity="success" variant="filled">
            Product type updated
          </Alert>
        </Snackbar>
      </SubscriptionAction>
    </React.Fragment>
  );
};

export default ChangeProductType;
